import BaseApi, { FetchMethodType } from 'api/BaseApi';
import { BorrowerType } from 'enums/BorrowerType';
import { IDataViewSortingType, FormLayoutData, VariableValue } from 'api/types';
import formatDateValue from 'utils/value-formatters/formatDateValue';
import DateTimeFormat from 'enums/DateTimeFormat';
import { IBaseAccountParams } from './base/BaseAccountsApi';

export enum ApplicationStatusType {
  Custom = 'custom',
  Initial = 'initial',
  Approve = 'approve',
  Reject = 'reject'
}

export interface IApplicationStatus {
  id: string;
  name: string;
  type: ApplicationStatusType;
  color: string;
  alternateStatusName?: string | null;
}

export interface IApplicationViewModel {
  id: string;
  displayId: string;
  status: IApplicationStatus;
  productId: string;
  productName: string;
  createdAt: string;
  incompleteTasksCount: number;
  borrowerId: string;
  intermediaryId?: string;
  variables: FormLayoutData;
  updatedAt: string;
  borrower: IBaseAccountParams;
  coborrowerTypes: BorrowerType[];
  coborrowerIds: string[];
  testing?: boolean;
  borrowerType: BorrowerType;
  statusDate?: string;
  loanAmount?: number;
  rejectedAt?: Date;
  approvedAt?: Date;
  declineReasons?: string[];
}

export type IBorrowerApplicationsSortingType = IDataViewSortingType<ApplicationSortingField>;

interface IGetBorrowerApplicationsRequest {
  count: number;
  offset: number;
  search?: string;
  productId?: string;
  createdAtFrom?: Date | null;
  createdAtTo?: Date | null;
  onlyInFinalStatus?: boolean;
  onlyInProgress?: boolean;
  onlyInApprovedStatus?: boolean;
  onlyInRejectedStatus?: boolean;
  statusIds?: string[];
}

export interface IGetBorrowerApplicationsResponse {
  applications: IApplicationViewModel[];
  total: number;
}

export interface ICreateBorrowerParams {
  type: BorrowerType;
  variables: Record<string, VariableValue>;
}

export interface ICreateApplicationParams {
  productId: string;
  coborrowers?: ICreateBorrowerParams[];
  intermediaryId?: string;
}

export interface ICreateApplicationByIntermediaryParams {
  productId: string;
  borrower: ICreateBorrowerParams;
  coborrowers?: ICreateBorrowerParams[];
}

export enum ApplicationSortingField {
  CreatedAt = 'createdAt',
  DisplayId = 'displayId',
  LoanAmount = 'loanAmount',
  BorrowerFullName = 'borrowerFullName',
  Email = 'borrowerEmail',
  Phone = 'borrowerPhoneNumber',
}

export interface IApplicationsCount {
  inProgress: number;
  approved: number;
  rejected: number;
}

export type ApplicationsSortingType = IDataViewSortingType<ApplicationSortingField>;

export interface IApplicationsApi {
  getBorrowerApplications(
    params: IGetBorrowerApplicationsRequest,
    sortingType: IBorrowerApplicationsSortingType,
  ): Promise<IGetBorrowerApplicationsResponse>;
  getCurrentApplication(): Promise<IApplicationViewModel | null>;
  createApplication(params: ICreateApplicationParams): Promise<IApplicationViewModel>;
  countApplications(): Promise<IApplicationsCount>;
}

export default class ApplicationsRestApi extends BaseApi implements IApplicationsApi {
  public getBorrowerApplications(
    params: IGetBorrowerApplicationsRequest,
    sortingType: IBorrowerApplicationsSortingType,
  ): Promise<IGetBorrowerApplicationsResponse> {
    const urlParams = this.getUrlSearchParams(params, sortingType);

    return this.fetch(`/applications?${urlParams}`);
  }

  public async getCurrentApplication(): Promise<IApplicationViewModel | null> {
    const { application } = await this.fetch<{ application: IApplicationViewModel | null }>('/applications/current');

    return application;
  }

  public createApplication(params: ICreateApplicationParams): Promise<IApplicationViewModel> {
    const { productId } = params;

    return this.fetch('/applications', {
      method: FetchMethodType.POST,
      body: {
        productId,
        variables: {},
        coborrowers: params.coborrowers,
        intermediaryId: params.intermediaryId,
      },
    });
  }

  public createApplicationByIntermediary(params: ICreateApplicationByIntermediaryParams): Promise<IApplicationViewModel> {
    const { productId } = params;

    return this.fetch('/applications', {
      method: FetchMethodType.POST,
      body: {
        productId,
        variables: {},
        borrower: params.borrower,
        coborrowers: params.coborrowers,
      },
    });
  }

  public findByDisplayId(displayId: string) {
    return this.fetch<IApplicationViewModel>(`/applications/${displayId}`);
  }

  public countApplications(): Promise<IApplicationsCount> {
    return this.fetch('/applications/count');
  }

  private getUrlSearchParams(params: IGetBorrowerApplicationsRequest, sortingType: IBorrowerApplicationsSortingType) {
    const searchParams = new URLSearchParams();

    if (typeof params.offset !== 'undefined') {
      searchParams.set('offset', params.offset.toString());
    }

    if (params.count) {
      searchParams.set('count', params.count.toString());
    }

    if (params.search) {
      searchParams.set('search', params.search);
    }

    if (params.productId) {
      searchParams.set('productId', params.productId);
    }

    if (params.createdAtFrom) {
      searchParams.append('createdAtFrom', formatDateValue(params.createdAtFrom, {
        dateFormat: DateTimeFormat.ISOString,
      }));
    }

    if (params.createdAtTo) {
      searchParams.append('createdAtTo', formatDateValue(params.createdAtTo, {
        dateFormat: DateTimeFormat.ISOString,
      }));
    }

    if (params.onlyInFinalStatus) {
      searchParams.set('onlyInFinalStatus', params.onlyInFinalStatus.toString());
    }

    if (params.onlyInProgress) {
      searchParams.set('onlyInProgress', params.onlyInProgress.toString());
    }

    if (params.onlyInApprovedStatus) {
      searchParams.set('onlyInApprovedStatus', params.onlyInApprovedStatus.toString());
    }

    if (params.onlyInRejectedStatus) {
      searchParams.set('onlyInRejectedStatus', params.onlyInRejectedStatus.toString());
    }

    params.statusIds?.forEach((statusId) => {
      searchParams.set('statusIds', statusId);
    });

    searchParams.set('sortField', sortingType.field);
    searchParams.set('sortDirection', sortingType.ascending ? 'asc' : 'desc');

    return searchParams;
  }
}
